import { useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { useAuthStore } from '@/features/auth/store/authStore';
import { loginApi, registerApi, logoutApi } from '@/features/auth/services/auth.api';
import type { LoginFormValues, RegisterFormValues } from '@/features/auth/types/auth.types';

export function useAuth() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();

  const {
    user,
    accessToken,
    status,
    sessionId,
    rememberMe,
    setAuth,
    clearSession,
  } = useAuthStore();

  // ─── Login Mutation ───────────────────────────────────────────────

  const loginMutation = useMutation({
    mutationFn: (data: LoginFormValues) =>
      loginApi({
        email: data.email,
        password: data.password,
        rememberMe: data.rememberMe,
      }),
    onSuccess: (payload, variables) => {
      setAuth(payload, variables.rememberMe);
      queryClient.invalidateQueries({ queryKey: ['auth'] });
      toast.success(`Welcome back, ${payload.user.firstName}!`);
      // Honor ProtectedRoute's ?redirect= (the page the user was on before
      // being bounced to login) — only ever a same-origin relative path
      // (ProtectedRoute builds it from location.pathname), but re-validated
      // here since the query param is still attacker-editable in the URL bar.
      const redirect = searchParams.get('redirect');
      const target = redirect && redirect.startsWith('/') && !redirect.startsWith('//') ? redirect : '/dashboard';
      navigate(target, { replace: true });
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Invalid email or password');
    },
  });

  // ─── Register Mutation (creates a new Company + its admin) ─────────

  const registerMutation = useMutation({
    mutationFn: (data: RegisterFormValues) =>
      registerApi({
        companyName: data.companyName,
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email,
        password: data.password,
      }),
    onSuccess: (payload) => {
      setAuth(payload, false);
      queryClient.invalidateQueries({ queryKey: ['auth'] });
      toast.success(`Welcome, ${payload.user.firstName}! Your company is ready.`);
      navigate('/dashboard', { replace: true });
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Could not create your account');
    },
  });

  // ─── Logout Mutation (refresh cookie is sent automatically) ────────

  const logoutMutation = useMutation({
    mutationFn: async () => {
      await logoutApi();
    },
    onSettled: () => {
      clearSession();
      queryClient.clear();
      toast.success('Logged out successfully');
      navigate('/login', { replace: true });
    },
  });

  // ─── Login Handler ────────────────────────────────────────────────

  const login = useCallback(
    (data: LoginFormValues) => {
      loginMutation.mutate(data);
    },
    [loginMutation],
  );

  // ─── Register Handler ─────────────────────────────────────────────

  const registerAccount = useCallback(
    (data: RegisterFormValues) => {
      registerMutation.mutate(data);
    },
    [registerMutation],
  );

  // ─── Logout Handler ───────────────────────────────────────────────

  const logout = useCallback(() => {
    logoutMutation.mutate();
  }, [logoutMutation]);

  return {
    user,
    accessToken,
    status,
    sessionId,
    rememberMe,
    isAuthenticated: status === 'authenticated',
    isLoading: status === 'idle' || status === 'loading',
    isLoggingIn: loginMutation.isPending,
    isLoggingOut: logoutMutation.isPending,
    isRegistering: registerMutation.isPending,
    loginError: loginMutation.error,
    registerError: registerMutation.error,
    login,
    logout,
    registerAccount,
  };
}
